import { Form, Input, Modal, Popover, Switch } from 'antd';
import { FC, useState } from 'react';
import { AiOutlineMail } from 'react-icons/ai';
import { MdOutlineQuestionMark } from 'react-icons/md';
import { useSelector } from 'react-redux';
import { RootState } from 'src/store';
import apiRequests from 'src/utils/api';
import apiRoutes from 'src/utils/apiRoutes';
import asyncErrorHandler from 'src/utils/asyncErrorHandler';
import { getCurrentCustomer } from 'src/utils/localStorage';
import RequestsSelect from 'src/components/RequestsSelect';

interface InviteModalProps {
  visible: boolean;
  onInviteSent?: () => void;
  onClose: () => void;
}

const InviteModal: FC<InviteModalProps> = ({
  visible,
  onInviteSent,
  onClose,
}) => {
  const project = useSelector(
    (globalState: RootState) => globalState.projectRetainer.data
  );

  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();
  const strictRequests = Form.useWatch('strict_requests', form);

  const showSentInviteModal = (email: string) => {
    Modal.success({
      title: 'Invite sent successfully!',
      content: (
        <p>
          The invitation was successfully sent to <b>{email}</b> email.
        </p>
      ),
      footer: null,
      maskClosable: true,
    });
  };

  const submit = async () => {
    setLoading(true);

    try {
      const data = await form.validateFields();

      if (!data.strict_requests) {
        delete data.requests;
      }

      const currentCustomer = getCurrentCustomer();

      try {
        await apiRequests.post(`${apiRoutes.CUSTOMER_INVITE}`, {
          ...data,
          customer_id: currentCustomer.uuid,
          advertising: false,
          creative_package: false,
          leadsie_manage_link: false,
          leadsie_social_link: false,
          login_register: true,
          schedule_call_link: false,
          social_media: false,
          video_production: false,
          website: false,
          welcome_kit: false,
        });

        onClose();
        onInviteSent?.();

        showSentInviteModal(data.email);
      } catch (error) {
        asyncErrorHandler(error);
      }
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      destroyOnClose
      confirmLoading={loading}
      title="Send an invite"
      open={visible}
      onCancel={onClose}
      onOk={submit}
      okText="Invite"
      afterClose={() => {
        form.resetFields();
      }}
    >
      <Form form={form} layout="vertical" className="default-form" size="large">
        <Form.Item
          label="E-mail"
          name="email"
          rules={[
            {
              required: true,
              type: 'email',
              message: 'Please input a e-mail!',
            },
          ]}
        >
          <Input
            prefix={<AiOutlineMail />}
            placeholder="E-mail to send the invite"
          />
        </Form.Item>
        <Form.Item
          label={
            <span className="flex items-center gap-1">Restrict access to
              <Popover content="User can only see requests he is related to">
                <div className='flex items-center gap-2 bg-[#eff5ff] rounded-full p-1 text-xs'>
                  <MdOutlineQuestionMark />
                </div>
              </Popover>
            </span>
          }
          name="strict_requests"
        >
          <Switch />
        </Form.Item>

        {strictRequests && (
          <Form.Item label="Request access" name="requests">
            <RequestsSelect projectUuid={project?.uuid} />
          </Form.Item>
        )}
      </Form>
    </Modal>
  );
};

export default InviteModal;
